import clsx from 'clsx'
import { FC, forwardRef } from 'react'
import { PropsWithChildren } from 'react'

export type StretchSize = '25' | '33' | '50' | '75' | boolean

export type RoundedStyle = 'top' | 'bottom' | 'start' | 'end' | boolean

export type ShadowStyle = 'sm' | 'lg' | 'none'

export type KTCardProps = {
  className?: string
  shadow?: ShadowStyle // https://getbootstrap.com/docs/5.2/utilities/shadows/
  flush?: boolean // https://preview.keenthemes.com/html/metronic/docs/base/cards#flush-borders
  resetSidePaddings?: boolean // https://preview.keenthemes.com/html/metronic/docs/base/cards#reset-side-paddings
  bordered?: boolean // https://preview.keenthemes.com/html/metronic/docs/base/cards#bordered-style
  dashed?: boolean // https://preview.keenthemes.com/html/metronic/docs/base/cards#dashed-style
  stretchXs?: StretchSize // https://preview.keenthemes.com/html/metronic/docs/base/cards#card-stretch
  stretchSm?: StretchSize
  stretchMd?: StretchSize
  stretchLg?: StretchSize
  stretchXl?: StretchSize
  stretchXxl?: StretchSize
  rounded?: RoundedStyle // https://preview.keenthemes.com/html/metronic/docs/base/cards#utility-classes
  utilityP?: number
  utilityPY?: number
  utilityPX?: number
  utilityMB?: number
}

const Card: FC<PropsWithChildren<KTCardProps>> = (props) => {
  const {
    className,
    shadow,
    flush,
    resetSidePaddings,
    bordered,
    dashed,
    stretchXs,
    stretchSm,
    stretchMd,
    stretchLg,
    stretchXl,
    stretchXxl,
    rounded,
    utilityP,
    utilityPY,
    utilityPX,
    utilityMB = 6,
    children,
  } = props
  return (
    <div
      className={clsx(
        'card',
        {
          'card-flush': flush,
          'card-px-0': resetSidePaddings,
          'card-bordered': bordered,
          'card-dashed': dashed,
        },
        shadow && `shadow-${shadow}`,
        rounded ? (rounded === true ? 'card-rounded' : `card-rounded-${rounded}`) : undefined,
        stretchXs ? (stretchXs === true ? 'card-stretch' : `card-stretch-${stretchXs}`) : undefined,
        stretchSm ? (stretchSm === true ? 'card-sm-stretch' : `card-sm-stretch-${stretchSm}`) : undefined,
        stretchMd ? (stretchMd === true ? 'card-md-stretch' : `card-md-stretch-${stretchMd}`) : undefined,
        stretchLg ? (stretchLg === true ? 'card-lg-stretch' : `card-lg-stretch-${stretchLg}`) : undefined,
        stretchXl ? (stretchXl === true ? 'card-xl-stretch' : `card-xl-stretch-${stretchXl}`) : undefined,
        stretchXxl ? (stretchXxl === true ? 'card-xxl-stretch' : `card-xxl-stretch-${stretchXxl}`) : undefined,
        utilityP && `p-${utilityP}`,
        utilityPX && `px-${utilityPX}`,
        utilityPY && `py-${utilityPY}`,
        `mb-${utilityMB}`,
        className
      )}
    >
      {children}
    </div>
  )
}

export type KTCardBodyProps = {
  className?: string
  scroll?: boolean
  height?: number
}

const Body = forwardRef<HTMLDivElement, PropsWithChildren<KTCardBodyProps>>((props, ref) => {
  const { className, scroll, height, children } = props
  return (
    <div
      ref={ref}
      className={clsx(
        'card-body',
        className && className,
        {
          'card-scroll': scroll,
        },
        height && `h-${height}px`
      )}
    >
      {children}
    </div>
  )
})

export type KTCardFooterProps = {
  className?: string
}

const Footer: FC<PropsWithChildren<KTCardFooterProps>> = (props) => {
  const { className, children } = props
  return <div className={clsx('card-footer', className && className)}>{children}</div>
}

export type KTCardHeaderProps = {
  className?: string
}

const Header: FC<PropsWithChildren<KTCardHeaderProps>> = ({ children, className }) => {
  return <div className={clsx('card-header', className)}>{children}</div>
}

export type KTCardTitleProps = {}

const Title: FC<PropsWithChildren<KTCardTitleProps>> = ({ children }) => {
  return <h3 className="card-title fw-medium">{children}</h3>
}

export type KTCardToolbarProps = {
  className?: string
}

const Toolbar: FC<PropsWithChildren<KTCardToolbarProps>> = ({ children, className }) => {
  return <div className={clsx('card-toolbar', className)}>{children}</div>
}

const KTCard = Object.assign(Card, {
  Header,
  Title,
  Toolbar,
  Body,
  Footer,
})

export default KTCard
